/**
 * The API client — roadmap 4.1, §4.2.
 *
 * Types are **generated** from the committed OpenAPI artifact into
 * `schema.d.ts` (`make client`), never hand-written. CI regenerates and diffs
 * both artifacts, so renaming a serializer field breaks the build here rather
 * than in production.
 *
 * What generation does not give us lives in this file:
 *
 * * **Cookie credentials.** The JWTs are HttpOnly (§8.2) — there is no token to
 *   attach, only cookies to send, and `credentials: 'include'` is what sends
 *   them.
 * * **CSRF** on unsafe methods — SEC-04.
 * * **`Accept-Language`**, which is what selects the Arabic column of every
 *   translated field (§12.2).
 * * **One refresh on a 401**, then handing over to whatever the app wants to do
 *   about it.
 */
import createClient, { type Middleware } from 'openapi-fetch'

import { i18n } from '@/shared/i18n'

import { toApiError } from './problem'
import type { paths } from './schema'

/**
 * The API's origin.
 *
 * Unset means this origin, which is the case worth optimising for: behind the
 * dev proxy and in production the SPA and the API share one, so the auth
 * cookies need no SameSite exceptions. It resolves to `location.origin` rather
 * than to an empty string because `openapi-fetch` builds a `Request`, and the
 * `Request` constructor rejects a relative URL outside a document context —
 * which is every test.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || location.origin

/**
 * The same origin `api` resolves paths against, for the few things the browser
 * fetches itself rather than through `openapi-fetch`.
 *
 * There are no consumers today. Its one user was the ticket QR `<img>` at
 * `/api/v1/tickets/{id}/qr.png`, which the SPA stopped using on 8 October 2026
 * when it began drawing the QR itself and the endpoint was disabled (Gap 55).
 * It is kept, not removed, so the Gap 55 decision can restore the image
 * without re-deriving it: an `<img src>` hardcoded to a relative path is right
 * only while the API base is empty.
 */
export const apiBaseUrl = BASE_URL

const REFRESH_PATH = '/api/v1/auth/refresh/'

/**
 * Paths where a 401 is an *answer*, not an expired session.
 *
 * Refreshing after a rejected login would replace "wrong password" with a
 * second failure from a different endpoint, and refreshing after a failed
 * refresh is an infinite loop.
 */
const NO_REFRESH_PATHS = [
  '/api/v1/auth/login/',
  '/api/v1/auth/register/',
  REFRESH_PATH,
  '/api/v1/auth/password/reset/',
  '/api/v1/auth/password/reset/confirm/',
  '/api/v1/auth/verify-email/',
]

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

function csrfHeaders(method: string): Record<string, string> {
  if (SAFE_METHODS.has(method.toUpperCase())) return {}
  const token = readCookie('csrftoken')
  return token ? { 'X-CSRFToken': token } : {}
}

// ---------------------------------------------------------------------------
// session expiry
// ---------------------------------------------------------------------------
type UnauthenticatedHandler = () => void

let onUnauthenticated: UnauthenticatedHandler = () => {}

/**
 * Register what happens when the session cannot be recovered.
 *
 * A callback rather than `window.location = '/login'` in here: the router can
 * navigate without discarding the app, tests can assert on it, and this module
 * keeps no opinion about routing.
 */
export function setUnauthenticatedHandler(handler: UnauthenticatedHandler): void {
  onUnauthenticated = handler
}

/**
 * The in-flight refresh, if any.
 *
 * A page load fires several requests at once, and if the access cookie has
 * expired they all come back 401 together. Without this they would each rotate
 * the refresh token — and rotation with reuse detection (§8.2) reads the second
 * rotation as a stolen token and kills the whole family, logging the user out
 * for doing nothing but loading a page.
 */
let refreshInFlight: Promise<boolean> | null = null

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= fetch(`${BASE_URL}${REFRESH_PATH}`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders('POST'),
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

// ---------------------------------------------------------------------------
// middleware
// ---------------------------------------------------------------------------
const headers: Middleware = {
  onRequest({ request }) {
    // The interface language also selects the content language: the API
    // resolves every translated field from this header (§12.2).
    const language = i18n.resolvedLanguage || i18n.language
    if (language) request.headers.set('Accept-Language', language)

    for (const [name, value] of Object.entries(csrfHeaders(request.method))) {
      request.headers.set(name, value)
    }

    return request
  },
}

/**
 * Clones kept so a 401 can be retried.
 *
 * A `Request` body is a stream that `fetch` consumes, and cloning after the
 * fact throws. The clone has to be taken before the request goes out, and a
 * `WeakMap` means it is collected with the request rather than leaving one copy
 * of every POST body alive for the life of the page.
 */
const retryable = new WeakMap<Request, Request>()

const refreshOn401: Middleware = {
  onRequest({ request }) {
    if (!NO_REFRESH_PATHS.some((path) => request.url.includes(path))) {
      retryable.set(request, request.clone())
    }
    return request
  },

  async onResponse({ request, response }) {
    if (response.status !== 401) return response

    const retry = retryable.get(request)
    if (!retry) return response

    // Exactly one attempt. A second 401 after a successful refresh means the
    // session is genuinely gone rather than stale.
    retryable.delete(request)

    if (!(await refreshSession())) {
      onUnauthenticated()
      return response
    }

    return fetch(retry)
  },
}

export const api = createClient<paths>({
  baseUrl: BASE_URL,
  // HttpOnly cookies are the credential — §8.2. Nothing to read, everything to
  // send.
  credentials: 'include',
  headers: { Accept: 'application/json' },
  // Dereferenced per call. `openapi-fetch` otherwise binds whatever
  // `globalThis.fetch` was at import time, which is before Sentry installs its
  // instrumentation and before any test can replace it.
  fetch: (request: Request) => fetch(request),
})

api.use(headers, refreshOn401)

// ---------------------------------------------------------------------------
// unwrapping
// ---------------------------------------------------------------------------
type FetchResult<T> = {
  data?: T
  error?: unknown
  response: Response
}

/**
 * Turn `openapi-fetch`'s `{ data, error }` into a value, or throw an `ApiError`.
 *
 * TanStack Query decides retry, error and loading state from a rejected
 * promise, so the result object has to become an exception somewhere. Doing it
 * here keeps the generated call sites readable:
 *
 *     const me = await unwrap(api.GET('/api/v1/me/'))
 */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  const { data, error, response } = await promise

  if (!response.ok || error !== undefined) {
    throw toApiError(error, response, pathOf(response))
  }

  // 204, and any other success with no body. The generated type is `never`
  // there, so this cast describes reality rather than hiding a hole.
  return data as T
}

function pathOf(response: Response): string {
  try {
    return new URL(response.url).pathname
  } catch {
    // A synthesised Response (a test, a service worker) has no usable url.
    return ''
  }
}
